import React, { useState, useEffect } from 'react';
import { addonService, addonPriceService, locationService } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './AddOns.css';

const AddOns = () => {
  const [addons, setAddons] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'meal',
    description: '',
    base_price: '',
    is_vegetarian: false,
    is_non_vegetarian: false,
    is_active: true,
  });
  const [priceFormData, setPriceFormData] = useState({
    location: '',
    addon: '',
    price: '',
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));

  const fetchData = async () => {
    try {
      const [addonsRes, locationsRes] = await Promise.all([
        addonService.getAll(),
        locationService.getAll(),
      ]);
      setAddons(toList(addonsRes.data));
      setLocations(toList(locationsRes.data));
    } catch (error) {
      console.error('Error fetching data:', error);
      setAddons([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAddon) {
        await addonService.update(editingAddon.id, formData);
      } else {
        await addonService.create(formData);
      }
      setShowModal(false);
      setEditingAddon(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving addon:', error);
      alert('Error saving addon');
    }
  };

  const handlePriceSubmit = async (e) => {
    e.preventDefault();
    try {
      await addonPriceService.create(priceFormData);
      setShowPriceModal(false);
      resetPriceForm();
      fetchData();
    } catch (error) {
      console.error('Error saving price:', error);
      alert('Error saving price');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'meal',
      description: '',
      base_price: '',
      is_vegetarian: false,
      is_non_vegetarian: false,
      is_active: true,
    });
  };

  const resetPriceForm = () => {
    setPriceFormData({
      location: '',
      addon: '',
      price: '',
      is_active: true,
    });
  };

  if (loading) {
    return <div className="loading">Loading add-ons...</div>;
  }

  return (
    <div className="addons-page">
      <div className="page-header">
        <h1>Add-ons Management</h1>
        <div>
          <button className="btn-secondary" onClick={() => { setShowPriceModal(true); resetPriceForm(); }}>
            Set Add-on Price
          </button>
          <button className="btn-primary" onClick={() => { setShowModal(true); setEditingAddon(null); resetForm(); }}>
            <FiPlus />
            Add Add-on
          </button>
        </div>
      </div>

      <div className="addons-list">
        {addons.map((addon) => (
          <div key={addon.id} className="addon-card">
            <div className="addon-info">
              <h3>{addon.name}</h3>
              <p className="addon-type">{addon.type}</p>
              <p className="addon-description">{addon.description}</p>
              <p className="addon-price">Base Price: ₹ {parseFloat(addon.base_price).toFixed(2)}</p>
              <div className="dietary-badges">
                {addon.is_vegetarian && <span className="badge veg">Veg</span>}
                {addon.is_non_vegetarian && <span className="badge non-veg">Non-Veg</span>}
              </div>
              <span className={`badge ${addon.is_active ? 'active' : 'inactive'}`}>
                {addon.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="addon-actions">
              <button className="btn-edit" onClick={() => { setEditingAddon(addon); setFormData({ ...addon }); setShowModal(true); }}>
                <FiEdit2 />
              </button>
              <button className="btn-delete" onClick={async () => {
                if (window.confirm('Are you sure?')) {
                  try {
                    await addonService.delete(addon.id);
                    fetchData();
                  } catch (error) {
                    alert('Error deleting addon');
                  }
                }
              }}>
                <FiTrash2 />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingAddon(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingAddon ? 'Edit Add-on' : 'Add Add-on'}</h2>
            <form onSubmit={handleAddonSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="meal">Meal</option>
                  <option value="buffet">Buffet</option>
                  <option value="fast_track">Fast Track</option>
                  <option value="locker">Locker</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Base Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                  required
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_vegetarian}
                    onChange={(e) => setFormData({ ...formData, is_vegetarian: e.target.checked })}
                  />
                  Vegetarian
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_non_vegetarian}
                    onChange={(e) => setFormData({ ...formData, is_non_vegetarian: e.target.checked })}
                  />
                  Non-Vegetarian
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingAddon(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPriceModal && (
        <div className="modal-overlay" onClick={() => { setShowPriceModal(false); resetPriceForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Set Add-on Price for Location</h2>
            <form onSubmit={handlePriceSubmit}>
              <div className="form-group">
                <label>Location *</label>
                <select
                  value={priceFormData.location}
                  onChange={(e) => setPriceFormData({ ...priceFormData, location: e.target.value })}
                  required
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Add-on *</label>
                <select
                  value={priceFormData.addon}
                  onChange={(e) => setPriceFormData({ ...priceFormData, addon: e.target.value })}
                  required
                >
                  <option value="">Select Add-on</option>
                  {addons.map(addon => (
                    <option key={addon.id} value={addon.id}>{addon.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={priceFormData.price}
                  onChange={(e) => setPriceFormData({ ...priceFormData, price: e.target.value })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowPriceModal(false); resetPriceForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOns;
